import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  ChevronLeft, 
  ChevronRight, 
  AlertCircle,
  RefreshCw,
  Calendar,
  TrendingDown,
  TrendingUp,
  Activity,
  ExternalLink,
  AlertTriangle,
  History
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "../components/ui/alert";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CashflowPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [showPreviousPeriod, setShowPreviousPeriod] = useState(false);
  
  // Date range state
  const today = new Date().toISOString().split('T')[0];
  const [range, setRange] = useState(() => {
    const r = searchParams.get("range") || "90";
    return parseInt(r) || 90;
  });

  // Calculate date range
  const { fromDate, toDate } = useMemo(() => {
    const from = new Date();
    from.setDate(from.getDate() - 30); // Start 30 days ago
    const to = new Date();
    to.setDate(to.getDate() + range);
    return {
      fromDate: from.toISOString().split('T')[0],
      toDate: to.toISOString().split('T')[0]
    };
  }, [range]);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/cashflow/range`, {
        params: { from_date: fromDate, to_date: toDate }
      });
      setData(response.data);
    } catch (err) {
      console.error("Error fetching cashflow:", err);
      setError("Failed to load cashflow data");
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => { document.title = "Cashflow - Floatly"; }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Update URL when range changes
  useEffect(() => {
    setSearchParams({ range: String(range) });
  }, [range, setSearchParams]);

  // Navigation functions
  const setRange30 = () => setRange(30);
  const setRange60 = () => setRange(60);
  const setRange90 = () => setRange(90);

  // Format currency
  const formatCurrency = (value, showSign = false) => {
    if (value === null || value === undefined) return "—";
    const num = parseFloat(value) || 0;
    const formatted = new Intl.NumberFormat("nl-NL", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Math.abs(num));
    
    if (showSign && num !== 0) {
      return num > 0 ? `+${formatted}` : `-${formatted}`;
    }
    return num < 0 ? `-${formatted}` : formatted;
  };

  // Format date
  const formatDate = (dateStr) => {
    const d = new Date(dateStr + "T00:00:00");
    const weekday = d.toLocaleDateString('en-US', { weekday: 'short' });
    const month = d.toLocaleDateString('en-US', { month: 'short' });
    const day = d.getDate();
    return `${weekday}, ${month} ${day}`;
  };

  // Get cell class based on value
  const getCellClass = (value, isOutflow = false) => {
    if (value === null || value === undefined || value === 0) {
      return "text-muted-foreground";
    }
    if (isOutflow) {
      return value > 0 ? "text-red-600" : "text-green-600";
    }
    return value > 0 ? "text-green-600" : "text-red-600";
  };

  // Check if row has low cash warning
  const isLowCash = (expected) => {
    return expected !== null && expected < 10000;
  };

  const summary = data?.summary || {};
  const rows = data?.rows || [];
  
  // Find the most recent bank balance date and filter rows
  // NOTE: This useMemo must be called before any early returns to respect Rules of Hooks
  const { filteredRows, lastBankBalanceDate } = useMemo(() => {
    if (!rows.length) return { filteredRows: [], lastBankBalanceDate: null };
    
    // Find the most recent date with a bank balance
    let lastBankDate = null;
    for (const row of rows) {
      if (row.bank_balance !== null && row.bank_balance !== undefined) {
        if (!lastBankDate || row.date > lastBankDate) {
          lastBankDate = row.date;
        }
      }
    }
    
    // If showPreviousPeriod is off, filter to only show from last bank balance date onwards
    if (!showPreviousPeriod && lastBankDate) {
      const filtered = rows.filter(row => row.date >= lastBankDate);
      return { filteredRows: filtered, lastBankBalanceDate: lastBankDate };
    }
    
    return { filteredRows: rows, lastBankBalanceDate: lastBankDate };
  }, [rows, showPreviousPeriod]);

  // Calculate summary stats from filteredRows to ensure consistency with visible data
  const filteredSummary = useMemo(() => {
    if (!filteredRows.length) {
      return { net_change: 0, lowest_position: null, lowest_date: null, movement_count: 0, last_bank_balance: summary.last_bank_balance };
    }
    
    // Calculate net change from filtered rows
    const netChange = filteredRows.reduce((sum, row) => sum + (row.daily_net || 0), 0);
    
    // Find lowest position in filtered rows
    let lowestPosition = null;
    let lowestDate = null;
    for (const row of filteredRows) {
      if (row.expected_position !== null && row.expected_position !== undefined) {
        if (lowestPosition === null || row.expected_position < lowestPosition) {
          lowestPosition = row.expected_position;
          lowestDate = row.date;
        }
      }
    }
    
    // Count movements (rows with any inflow or outflow)
    const movementCount = filteredRows.filter(row => 
      row.total_inflows > 0 || row.total_outflows > 0
    ).length;
    
    return {
      net_change: netChange,
      lowest_position: lowestPosition,
      lowest_date: lowestDate,
      movement_count: movementCount,
      last_bank_balance: summary.last_bank_balance
    };
  }, [filteredRows, summary.last_bank_balance]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="error-state">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-destructive" />
          <h2 className="text-2xl font-semibold mb-2">Something went wrong</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button onClick={fetchData} data-testid="retry-button">
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" data-testid="cashflow-page">
      {/* Header */}
      <header className="border-b bg-white shrink-0">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="page-title">
                Cashflow
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Aggregated cash position forecast
              </p>
            </div>
            <div className="flex items-center gap-6">
              {/* Summary Stats - derived from filteredRows for consistency */}
              {!loading && data && (
                <div className="flex items-center gap-6 text-sm">
                  <div className="text-center">
                    <p className="text-muted-foreground">Net {range}d</p>
                    <p className={`font-semibold font-mono ${filteredSummary.net_change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(filteredSummary.net_change, true)}
                    </p>
                  </div>
                  {filteredSummary.lowest_position !== null && (
                    <div className="text-center">
                      <p className="text-muted-foreground">Lowest</p>
                      <p className={`font-semibold font-mono ${filteredSummary.lowest_position < 10000 ? 'text-red-600' : 'text-foreground'}`}>
                        {formatCurrency(filteredSummary.lowest_position)}
                      </p>
                    </div>
                  )}
                  <div className="text-center">
                    <p className="text-muted-foreground">Movements</p>
                    <p className="font-semibold">{filteredSummary.movement_count || 0}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Controls Bar */}
      <div className="border-b bg-white/80 backdrop-blur sticky top-0 z-30 shrink-0">
        <div className="px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                variant={range === 30 ? "default" : "outline"}
                size="sm"
                onClick={setRange30}
                className="rounded-full"
                data-testid="range-30-button"
              >
                30 days
              </Button>
              <Button
                variant={range === 60 ? "default" : "outline"}
                size="sm"
                onClick={setRange60}
                className="rounded-full"
                data-testid="range-60-button"
              >
                60 days
              </Button>
              <Button
                variant={range === 90 ? "default" : "outline"}
                size="sm"
                onClick={setRange90}
                className="rounded-full"
                data-testid="range-90-button"
              >
                90 days
              </Button>
              
              {/* Show Previous Period Toggle */}
              <div className="flex items-center gap-2 ml-4 pl-4 border-l">
                <Switch
                  id="show-previous"
                  checked={showPreviousPeriod}
                  onCheckedChange={setShowPreviousPeriod}
                  data-testid="show-previous-toggle"
                />
                <Label htmlFor="show-previous" className="text-sm text-muted-foreground cursor-pointer flex items-center gap-1.5">
                  <History className="w-3.5 h-3.5" />
                  Show previous period
                </Label>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">
                {fromDate} → {toDate}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchData}
                className="rounded-full"
                data-testid="refresh-button"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 overflow-auto px-6 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="loading-state">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : filteredSummary.last_bank_balance == null ? (
          <div className="max-w-2xl mx-auto">
            <Alert className="bg-amber-50 border-amber-200" data-testid="no-bank-balance-alert">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertTitle className="text-amber-800 font-semibold">No Bank Balance Data</AlertTitle>
              <AlertDescription className="text-amber-700">
                <p className="mb-3">
                  To calculate expected cash positions, you need to enter your daily bank balances first.
                </p>
                <Link to="/bank-balance">
                  <Button variant="outline" size="sm" className="mt-2">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Go to Bank Balance
                  </Button>
                </Link>
              </AlertDescription>
            </Alert>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="empty-state">
            <Activity className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No cash movements</h3>
            <p className="text-muted-foreground mb-4">No planned cash events in the selected date range.</p>
          </div>
        ) : (
          <>
            {/* Low Cash Warning - uses filteredSummary for consistency with visible data */}
            {filteredSummary.lowest_position !== null && filteredSummary.lowest_position < 10000 && (
              <Alert className="mb-4 bg-red-50 border-red-200 max-w-5xl mx-auto" data-testid="low-cash-alert">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <AlertTitle className="text-red-800 font-semibold">Low Cash Warning</AlertTitle>
                <AlertDescription className="text-red-700">
                  Expected cash position drops to <strong>{formatCurrency(filteredSummary.lowest_position)}</strong> on {formatDate(filteredSummary.lowest_date)}.
                  Consider reviewing upcoming payments or arranging additional funding.
                </AlertDescription>
              </Alert>
            )}

            {/* Cashflow Table */}
            <div className="bg-white border rounded-xl shadow-sm">
              <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: "calc(100vh - 280px)", maxWidth: "100%" }}>
                <table className="w-full text-xs min-w-[1200px] border-collapse" data-testid="cashflow-table">
                  <thead className="sticky top-0 z-30">
                    <tr className="bg-white border-b">
                      <th className="text-left px-2 py-2 font-medium text-muted-foreground uppercase tracking-wider sticky left-0 bg-white z-40 w-[100px] min-w-[100px] border-r shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                        DATE
                      </th>
                      <th className="text-right px-2 py-2 font-medium text-muted-foreground uppercase tracking-wider" title="Salaries">
                        SAL.
                      </th>
                      <th className="text-right px-2 py-2 font-medium text-muted-foreground uppercase tracking-wider" title="Wage Tax">
                        WT
                      </th>
                      <th className="text-right px-2 py-2 font-medium text-muted-foreground uppercase tracking-wider" title="Taxes (VAT & Corporate)">
                        TAXES
                      </th>
                      <th className="text-right px-2 py-2 font-medium text-muted-foreground uppercase tracking-wider" title="Management Fees">
                        MGTFEE
                      </th>
                      <th className="text-right px-2 py-2 font-medium text-muted-foreground uppercase tracking-wider" title="Repayments">
                        REPAYM.
                      </th>
                      <th className="text-right px-2 py-2 font-medium text-muted-foreground uppercase tracking-wider" title="Purchase Invoices">
                        PURCH.
                      </th>
                      <th className="text-right px-2 py-2 font-medium text-muted-foreground uppercase tracking-wider" title="Sales Invoices">
                        SALES
                      </th>
                      <th className="text-right px-2 py-2 font-medium text-muted-foreground uppercase tracking-wider" title="Expected Revenue">
                        EXP.REV.
                      </th>
                      <th className="text-right px-2 py-2 font-medium text-red-600 uppercase tracking-wider bg-red-50" title="Total Outflows">
                        OUT
                      </th>
                      <th className="text-right px-2 py-2 font-medium text-green-600 uppercase tracking-wider bg-green-50" title="Total Inflows">
                        IN
                      </th>
                      <th className="text-right px-2 py-2 font-medium text-muted-foreground uppercase tracking-wider bg-muted/50" title="Daily Balance">
                        BALANCE
                      </th>
                      <th className="text-right px-2 py-2 font-medium text-muted-foreground uppercase tracking-wider" title="Bank Balance">
                        BANK
                      </th>
                      <th className="text-right px-2 py-2 font-medium text-primary uppercase tracking-wider bg-white sticky right-0 z-40 border-l w-[90px] min-w-[90px] shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)]" title="Expected Cash Position">
                        EXP.CASH
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((row, index) => {
                      const lowCash = isLowCash(row.expected_position);
                      const isToday = row.date === today;
                      const isLastBankBalance = row.date === lastBankBalanceDate;
                      
                      return (
                        <tr 
                          key={row.date}
                          className={`
                            border-b last:border-b-0 transition-colors
                            ${lowCash ? 'bg-amber-50 hover:bg-amber-100' : index % 2 === 0 ? 'bg-white hover:bg-muted/20' : 'bg-muted/5 hover:bg-muted/20'}
                            ${isToday ? 'ring-2 ring-inset ring-primary/30' : ''}
                            ${isLastBankBalance && !showPreviousPeriod ? 'border-t-2 border-t-primary/30' : ''}
                          `}
                          data-testid={`row-${row.date}`}
                        >
                          <td className={`px-2 py-1.5 font-medium sticky left-0 z-20 border-r shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] ${lowCash ? 'bg-amber-50' : index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                            {formatDate(row.date)}
                            {isToday && <span className="ml-1 text-primary">•</span>}
                            {isLastBankBalance && row.bank_balance !== null && <span className="ml-1 text-blue-500" title="Last bank balance">💰</span>}
                          </td>
                          <td className={`px-2 py-1.5 text-right font-mono ${getCellClass(row.salaries, true)}`}>
                            {row.salaries > 0 ? formatCurrency(row.salaries) : "—"}
                          </td>
                          <td className={`px-2 py-1.5 text-right font-mono ${getCellClass(row.payroll_tax, true)}`}>
                            {row.payroll_tax > 0 ? formatCurrency(row.payroll_tax) : "—"}
                          </td>
                          <td className={`px-2 py-1.5 text-right font-mono ${getCellClass(row.taxes_total, true)}`}>
                            {row.taxes_total !== 0 ? formatCurrency(row.taxes_total) : "—"}
                          </td>
                          <td className={`px-2 py-1.5 text-right font-mono ${getCellClass(row.management_fees + row.other_fees, true)}`}>
                            {(row.management_fees + row.other_fees) > 0 ? formatCurrency(row.management_fees + row.other_fees) : "—"}
                          </td>
                          <td className={`px-2 py-1.5 text-right font-mono ${getCellClass(row.repayments, true)}`}>
                            {row.repayments > 0 ? formatCurrency(row.repayments) : "—"}
                          </td>
                          <td className={`px-2 py-1.5 text-right font-mono ${getCellClass(row.purchase_invoices, true)}`}>
                            {row.purchase_invoices > 0 ? formatCurrency(row.purchase_invoices) : "—"}
                          </td>
                          <td className={`px-2 py-1.5 text-right font-mono ${getCellClass(row.sales_invoices)}`}>
                            {row.sales_invoices > 0 ? formatCurrency(row.sales_invoices) : "—"}
                          </td>
                          <td className={`px-2 py-1.5 text-right font-mono ${getCellClass(row.revenue_forecast)}`}>
                            {row.revenue_forecast > 0 ? formatCurrency(row.revenue_forecast) : "—"}
                          </td>
                          <td className={`px-2 py-1.5 text-right font-mono font-semibold text-red-600 bg-red-50/50`}>
                            {row.total_outflows > 0 ? formatCurrency(row.total_outflows) : "—"}
                          </td>
                          <td className={`px-2 py-1.5 text-right font-mono font-semibold text-green-600 bg-green-50/50`}>
                            {row.total_inflows > 0 ? formatCurrency(row.total_inflows) : "—"}
                          </td>
                          <td className={`px-2 py-1.5 text-right font-mono font-semibold bg-muted/30 ${row.daily_net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {row.daily_net !== 0 ? formatCurrency(row.daily_net, true) : "—"}
                          </td>
                          <td className={`px-2 py-1.5 text-right font-mono ${row.bank_balance !== null ? 'text-foreground font-semibold' : 'text-muted-foreground'}`}>
                            {row.bank_balance !== null ? formatCurrency(row.bank_balance) : "—"}
                          </td>
                          <td className={`px-2 py-1.5 text-right font-mono font-bold sticky right-0 z-20 border-l shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)] ${
                            lowCash ? 'bg-amber-50' : index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                          } ${
                            row.expected_position === null ? 'text-muted-foreground' :
                            row.expected_position < 0 ? 'text-red-600' :
                            row.expected_position < 10000 ? 'text-amber-600' :
                            'text-green-600'
                          }`}>
                            {row.expected_position !== null ? formatCurrency(row.expected_position) : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-muted/50 border-t-2 sticky bottom-0 z-30">
                    <tr>
                      <td className="px-2 py-2 font-semibold sticky left-0 bg-muted/50 z-20 border-r shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                        Totals
                      </td>
                      <td className="px-2 py-2 text-right font-mono font-semibold text-red-600">
                        {formatCurrency(filteredRows.reduce((s, r) => s + r.salaries, 0))}
                      </td>
                      <td className="px-2 py-2 text-right font-mono font-semibold text-red-600">
                        {formatCurrency(filteredRows.reduce((s, r) => s + r.payroll_tax, 0))}
                      </td>
                      <td className="px-2 py-2 text-right font-mono font-semibold text-red-600">
                        {formatCurrency(filteredRows.reduce((s, r) => s + r.taxes_total, 0))}
                      </td>
                      <td className="px-2 py-2 text-right font-mono font-semibold text-red-600">
                        {formatCurrency(filteredRows.reduce((s, r) => s + r.management_fees + r.other_fees, 0))}
                      </td>
                      <td className="px-2 py-2 text-right font-mono font-semibold text-red-600">
                        {formatCurrency(filteredRows.reduce((s, r) => s + r.repayments, 0))}
                      </td>
                      <td className="px-2 py-2 text-right font-mono font-semibold text-red-600">
                        {formatCurrency(filteredRows.reduce((s, r) => s + r.purchase_invoices, 0))}
                      </td>
                      <td className="px-2 py-2 text-right font-mono font-semibold text-green-600">
                        {formatCurrency(filteredRows.reduce((s, r) => s + r.sales_invoices, 0))}
                      </td>
                      <td className="px-2 py-2 text-right font-mono font-semibold text-green-600">
                        {formatCurrency(filteredRows.reduce((s, r) => s + r.revenue_forecast, 0))}
                      </td>
                      <td className="px-2 py-2 text-right font-mono font-bold text-red-600 bg-red-50/50">
                        {formatCurrency(filteredRows.reduce((s, r) => s + r.total_outflows, 0))}
                      </td>
                      <td className="px-2 py-2 text-right font-mono font-bold text-green-600 bg-green-50/50">
                        {formatCurrency(filteredRows.reduce((s, r) => s + r.total_inflows, 0))}
                      </td>
                      <td className={`px-2 py-2 text-right font-mono font-bold bg-muted/30 ${filteredRows.reduce((s, r) => s + r.daily_net, 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(filteredRows.reduce((s, r) => s + r.daily_net, 0), true)}
                      </td>
                      <td className="px-2 py-2"></td>
                      <td className="px-2 py-2 sticky right-0 bg-muted/50 z-20 border-l shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)]"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Legend */}
            <div className="mt-4 flex flex-wrap gap-4 text-xs text-muted-foreground max-w-5xl mx-auto">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-amber-100 border border-amber-300"></div>
                <span>Low cash warning (&lt;€10k)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-green-100 border border-green-300"></div>
                <span>Inflows</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-red-100 border border-red-300"></div>
                <span>Outflows</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-primary font-semibold">•</span>
                <span>Today</span>
              </div>
            </div>

          </>
        )}
      </main>
    </div>
  );
};

export default CashflowPage;
